import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { Loader } from '@/components/ui/loader'

const buttonVariants = cva('inline-flex flex-center whitespace-nowrap  font-semibold transition-all focus-visible:outline-none focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 active:opacity-60 active:scale-95 border-2 border-transparent relative  w-auto rounded-xl cursor-pointer leading-[-10px] select-none', {
	variants: {
		variant: {
			default: '!border-4 !border-border text-foreground !bg-primary/50 shadow-sm hover:bg-accent hover:text-accent-foreground',
			secondary: '!border-2 !border-border text-foreground !bg-none shadow-sm hover:bg-accent hover:text-accent-foreground',
			destructive: 'bg-none border-destructive text-destructive shadow-sm hover:bg-destructive/90',
			selected: '!border-2 !border-foreground text-foreground !bg-green-200/50 shadow-sm hover:bg-accent hover:text-accent-foreground',
			constructive: '!border-4 !border-foreground text-foreground !bg-primary/50 shadow-sm hover:bg-accent hover:text-accent-foreground  !border-green-800 !bg-green-200/50',
			warning: '!border !border-destructive/30 text-destructive/70 !bg-primary/50  hover:bg-accent hover:text-accent-foreground',
			muted: '!border-2 !border-foreground/50 text-foreground !bg-primary/50 shadow-sm hover:bg-accent hover:text-accent-foreground',
			ghost: '!bg-primary/20 shadow-sm hover:bg-accent hover:text-accent-foreground',
			link: 'text-primary underline-offset-4 hover:underline',
		},
		size: {
			default: 'h-11 px-4 text-xl',
			xs: '!h-6  px-2 !py-[14px] text-[11px] rounded-lg',
			sm: '!h-8  px-3 text-xs rounded-lg',
			lg: '!h-14 px-8 text-xl',
			xl: '!h-16 px-8 py-10 text-2xl ',
			chip: 'px-4 py-3 text-base  ',
			chipSm: 'px-3 py-1.5 text-base ',
			icon: 'h-9 w-9',
		},
	},
	defaultVariants: {
		variant: 'default',
		size: 'default',
	},
})

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean
	loading?: boolean
	icon?: React.ReactNode
	iconPosition?: 'left' | 'right'
	justifySpace?: boolean
	children?: React.ReactNode
	className?: any
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, icon, iconPosition = 'left', loading = false, justifySpace = false, children, ...props }, ref) => {
	const Comp = asChild ? Slot : 'button'

	// Handle icon-only buttons (when size is 'icon' and no children)
	const isIconOnly = size === 'icon' && !children

	// Single content element for Slot compatibility
	const buttonContent = (
		<div className='relative w-full h-full flex items-center justify-center leading-[85%]'>
			{/* Button content - always rendered but hidden during loading */}
			<div className={` flex w-full items-center ${justifySpace ? 'justify-between' : 'justify-center'} ${loading ? 'opacity-0' : 'opacity-100'}`}>
				{isIconOnly ? (
					icon
				) : icon ? (
					<span className={cn('flex', iconPosition === 'right' ? 'flex-row-reverse' : 'flex-row', justifySpace ? 'w-full' : '')}>
						<span className={cn('flex items-center', justifySpace && iconPosition === 'right' ? 'justify-end pl-2' : 'justify-start pr-2', className?.icon || '')}>{icon}</span>

						<span className={cn('flex items-center', justifySpace ? 'text-left flex-grow' : '', className?.children || '')}>{children}</span>
					</span>
				) : (
					children
				)}
			</div>

			{/* Loading overlay - positioned absolutely on top */}
			{loading && (
				<div className=' absolute flex items-center justify-center w-full h-full pointer-events-none'>
					<Loader visible={true} />
				</div>
			)}
		</div>
	)

	return (
		<Comp
			className={cn(
				buttonVariants({
					variant,
					size,
					className: typeof className === 'string' ? className : className?.button,
				}),
				'relative transition-all duration-500 ease-in-out',
			)}
			ref={ref}
			{...props}
			disabled={loading || props.disabled}>
			{buttonContent}
		</Comp>
	)
})

Button.displayName = 'Button'

export { Button, buttonVariants }
